import { useEffect } from 'react';
import { useWorkspaceActions } from './workspace-context';
import { useUiDispatch, useUiState } from './ui-state';
import { resolveWorkspaceShortcut } from './workspace-shortcuts';

/**
 * Atajos globales del lienzo de analisis (deshacer, copiar/pegar, borrar, paneles...).
 * Se ignoran cuando el foco esta en un campo editable.
 */
export function useWorkspaceShortcuts(): void {
  const { execute, undo, redo } = useWorkspaceActions();
  const ui = useUiState();
  const uiDispatch = useUiDispatch();
  const { selectedCardId, maximizedCardId, copiedCardId, aiPanelOpen } = ui;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const action = resolveWorkspaceShortcut(event, {
        selectedCardId,
        maximizedCardId,
        copiedCardId,
      });
      if (action === null) return;
      event.preventDefault();
      switch (action.type) {
        case 'undo':
          undo();
          break;
        case 'redo':
          redo();
          break;
        case 'delete-card':
          execute({ type: 'remove-card', cardId: action.cardId });
          if (maximizedCardId === action.cardId) {
            uiDispatch({ type: 'toggle-maximize', cardId: action.cardId });
          }
          uiDispatch({ type: 'select-card', cardId: null });
          break;
        case 'duplicate-card':
          execute({ type: 'duplicate-card', cardId: action.cardId });
          break;
        case 'copy-card':
          uiDispatch({ type: 'copy-card', cardId: action.cardId });
          break;
        case 'paste-card':
          execute({
            type: 'duplicate-card',
            cardId: action.cardId,
            ...(action.afterCardId === null ? {} : { afterCardId: action.afterCardId }),
          });
          break;
        case 'add-card':
          execute({ type: 'add-card' });
          break;
        case 'toggle-maximize':
        case 'clear-maximize':
          uiDispatch({ type: 'toggle-maximize', cardId: action.cardId });
          break;
        case 'deselect':
          uiDispatch({ type: 'select-card', cardId: null });
          break;
        case 'toggle-explorer':
          uiDispatch({ type: 'toggle-explorer' });
          break;
        case 'toggle-properties':
          uiDispatch({ type: 'toggle-properties' });
          break;
        case 'toggle-ai-panel':
          uiDispatch({ type: 'set-ai-panel', open: !aiPanelOpen });
          break;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [
    undo,
    redo,
    execute,
    uiDispatch,
    selectedCardId,
    maximizedCardId,
    copiedCardId,
    aiPanelOpen,
  ]);
}
